import { Badge } from '@repo/ui/components/ui/badge'
import { Button } from '@repo/ui/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@repo/ui/components/ui/card'
import { cn } from '@repo/ui/lib/utils'
import { ArrowRight, CheckCircle2, Loader2, RefreshCw, Unplug } from 'lucide-react'
import { toast } from 'sonner'
import { formatCalendarProvider, normalizeCalendarProvider } from '@/lib/calendar/calendar-status'
import type { CalendarConnectionSummary, CalendarProvider } from '@/lib/calendar/types'
import { trpc } from '@/lib/trpc'

interface CalendarConnectionCardProps {
  provider: CalendarProvider
  connection?: CalendarConnectionSummary
}

export function CalendarConnectionCard({ provider, connection }: CalendarConnectionCardProps) {
  const utils = trpc.useUtils()
  const connectMutation = trpc.calendar.connectUrl.useMutation({
    onSuccess: ({ url }) => {
      window.location.assign(url)
    },
    onError: (error) => toast.error('Calendar connection failed', { description: error.message }),
  })
  const disconnectMutation = trpc.calendar.connections.disconnect.useMutation({
    onSuccess: async () => {
      await utils.calendar.connections.list.invalidate()
      toast.success('Calendar disconnected')
    },
    onError: (error) =>
      toast.error('Could not disconnect calendar', { description: error.message }),
  })
  const syncMutation = trpc.calendar.syncNow.useMutation({
    onSuccess: () => toast.success('Calendar sync queued'),
    onError: (error) => toast.error('Could not queue sync', { description: error.message }),
  })
  const label = formatCalendarProvider(provider)
  const isConnected = connection?.status === 'active'
  const needsReauth = connection?.status === 'needs_reauth'
  const isConfigured = Boolean(connection && connection.status !== 'disconnected')
  const isBusy = connectMutation.isPending || disconnectMutation.isPending || syncMutation.isPending
  const statusLabel = isConnected ? 'Connected' : needsReauth ? 'Needs reauth' : 'Not connected'
  const iconSrc =
    provider === 'google'
      ? '/integrations/google-calendar.svg'
      : '/integrations/outlook-calendar.svg'
  const accentClass =
    provider === 'google'
      ? 'from-primary/10 via-primary/5 to-transparent'
      : 'from-accent/15 via-accent/5 to-transparent'

  return (
    <Card
      className={cn(
        'overflow-hidden rounded-[6px] border-border/70 bg-background shadow-none transition-colors hover:border-border',
        isConfigured ? 'bg-card/40' : 'bg-background'
      )}
    >
      <CardHeader
        className={cn(
          'relative flex flex-row items-start justify-between gap-3 p-4',
          'before:absolute before:inset-x-0 before:top-0 before:h-1 before:bg-gradient-to-r',
          accentClass
        )}
      >
        <div className="flex min-w-0 gap-3">
          <div className="grid size-11 shrink-0 place-items-center rounded-[5px] border border-border/70 bg-background shadow-sm">
            <img src={iconSrc} alt="" className="size-7 object-contain" loading="lazy" />
          </div>
          <div className="min-w-0">
            <CardTitle className="flex items-center gap-2 text-base">
              {label}
              {isConnected ? <CheckCircle2 className="size-4 text-primary" /> : null}
            </CardTitle>
            {connection ? (
              <p className="mt-1 truncate text-sm text-muted-foreground">
                {connection.providerAccountEmail}
              </p>
            ) : null}
          </div>
        </div>
        <Badge
          variant={isConnected || needsReauth ? 'secondary' : 'outline'}
          className="rounded-[4px]"
        >
          {statusLabel}
        </Badge>
      </CardHeader>
      {!isConfigured ? (
        <CardContent className="px-4 pt-0 pb-4">
          <Button
            size="sm"
            disabled={isBusy}
            className="justify-between"
            onClick={() =>
              connectMutation.mutate({
                provider: normalizeCalendarProvider(provider),
                returnTo: window.location.href,
              })
            }
          >
            <span className="flex items-center gap-2">
              {connectMutation.isPending ? <Loader2 className="size-3.5 animate-spin" /> : null}
              Connect {label}
            </span>
            <ArrowRight className="size-3.5" />
          </Button>
        </CardContent>
      ) : (
        <CardContent className="flex flex-wrap gap-2 px-4 pt-0 pb-4">
          {connection ? (
            <>
              {isConnected ? (
                <Button
                  variant="outline"
                  size="sm"
                  disabled={isBusy}
                  onClick={() => syncMutation.mutate({ connectionId: connection.id })}
                >
                  {syncMutation.isPending ? (
                    <Loader2 className="size-3.5 animate-spin" />
                  ) : (
                    <RefreshCw className="size-3.5" />
                  )}
                  Sync now
                </Button>
              ) : (
                <Button
                  variant="outline"
                  size="sm"
                  disabled={isBusy}
                  onClick={() =>
                    connectMutation.mutate({
                      provider: normalizeCalendarProvider(provider),
                      returnTo: window.location.href,
                    })
                  }
                >
                  {connectMutation.isPending ? (
                    <Loader2 className="size-3.5 animate-spin" />
                  ) : (
                    <RefreshCw className="size-3.5" />
                  )}
                  Reconnect
                </Button>
              )}
              <Button
                variant="ghost"
                size="sm"
                disabled={isBusy}
                onClick={() => disconnectMutation.mutate({ connectionId: connection.id })}
              >
                <Unplug className="size-3.5" />
                Disconnect
              </Button>
            </>
          ) : null}
        </CardContent>
      )}
    </Card>
  )
}
